import {type ReactNode} from 'react'
import {Link as RouterLink} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import './FreeRightInfoColumn.css'

interface FeatIcon {
  key: string
  icon: ReactNode
}

const iconStrokeProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

const FEATS: FeatIcon[] = [
  {
    key: 'save',
    icon: (
      <svg {...iconStrokeProps}>
        <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
        <path d="M17 21v-8H7v8M7 3v5h8" />
      </svg>
    ),
  },
  {
    key: 'scenes',
    icon: (
      <svg {...iconStrokeProps}>
        <path d="M12 2l9 5-9 5-9-5 9-5z" />
        <path d="M3 12l9 5 9-5" />
        <path d="M3 17l9 5 9-5" />
      </svg>
    ),
  },
  {
    key: 'carousel',
    icon: (
      <svg {...iconStrokeProps}>
        <rect x="2" y="5" width="6" height="14" rx="1.2" />
        <rect x="9" y="5" width="6" height="14" rx="1.2" />
        <rect x="16" y="5" width="6" height="14" rx="1.2" />
      </svg>
    ),
  },
  {
    key: 'device',
    icon: (
      <svg {...iconStrokeProps}>
        <rect x="6" y="2" width="12" height="20" rx="2.5" />
        <path d="M11 18h2" />
      </svg>
    ),
  },
  {
    key: 'catalog',
    icon: (
      <svg {...iconStrokeProps}>
        <rect x="3" y="3" width="7" height="7" rx="1.2" />
        <rect x="14" y="3" width="7" height="7" rx="1.2" />
        <rect x="3" y="14" width="7" height="7" rx="1.2" />
        <rect x="14" y="14" width="7" height="7" rx="1.2" />
      </svg>
    ),
  },
  {
    key: 'fork',
    icon: (
      <svg {...iconStrokeProps}>
        <circle cx="6" cy="5" r="2" />
        <circle cx="18" cy="5" r="2" />
        <circle cx="12" cy="19" r="2" />
        <path d="M6 7v3a3 3 0 003 3h6a3 3 0 003-3V7M12 13v4" />
      </svg>
    ),
  },
  {
    key: 'lights',
    icon: (
      <svg {...iconStrokeProps}>
        <circle cx="12" cy="12" r="3.5" />
        <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4" />
      </svg>
    ),
  },
  {
    key: 'text',
    icon: (
      <svg {...iconStrokeProps}>
        <path d="M5 6V4h14v2M10 4v16M14 4v16M8 20h8" />
      </svg>
    ),
  },
  {
    key: 'sprites',
    icon: (
      <svg {...iconStrokeProps}>
        <rect x="3" y="5" width="13" height="13" rx="1.5" />
        <rect x="8" y="9" width="13" height="10" rx="1.5" />
        <circle cx="6.5" cy="8.5" r="1" />
        <path d="M3 14l3.5-3 3 3" />
      </svg>
    ),
  },
  {
    key: 'noSub',
    icon: (
      <svg {...iconStrokeProps}>
        <path d="M20.6 13.4l-7.2 7.2a2 2 0 01-2.8 0L3 12.8V4h8.8l8.8 8.8a2 2 0 010 2.6z" />
        <circle cx="8.5" cy="8.5" r="1.3" />
      </svg>
    ),
  },
]

const arrowIcon = (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12h14" />
    <path d="M13 5l7 7-7 7" />
  </svg>
)

const FreeRightInfoColumn = () => {
  const {t} = useTranslation()

  return (
    <aside className="fric-root" aria-label={t('freeInfoColumn.ariaLabel')}>
      {/* Scrollable area — header + feature list. CTA below is pinned. */}
      <div className="fric-scroll">
        <div className="fric-header">
          <div className="fric-eyebrow">{t('freeInfoColumn.eyebrow')}</div>
          <h2 className="fric-heading">{t('freeInfoColumn.heading')}</h2>
          <p className="fric-sub">{t('freeInfoColumn.sub')}</p>
        </div>

        <ul className="fric-feats">
          {FEATS.map(({key, icon}) => (
            <li key={key} className="fric-feat">
              <span className="fric-feat-icon" aria-hidden>
                {icon}
              </span>
              <div className="fric-feat-body">
                <div className="fric-feat-label">{t(`freeInfoColumn.feats.${key}.label`)}</div>
                <div className="fric-feat-desc">{t(`freeInfoColumn.feats.${key}.desc`)}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA pinned at the bottom (mirrors the Export button in FreeLeftSidebar) */}
      <div className="fric-footer">
        <RouterLink to="/login" className="fric-cta">
          <span className="fric-cta-main">
            {t('freeInfoColumn.cta')}
            {arrowIcon}
          </span>
          <span className="fric-cta-hint">{t('freeInfoColumn.ctaHint')}</span>
        </RouterLink>
      </div>
    </aside>
  )
}

export default FreeRightInfoColumn
